// useTransactions.ts
import { useState, useEffect, useCallback } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { toast } from "react-hot-toast";
import { GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid";

const useTransactions = (columns: GridColDef[]) => {
  const [transactionsData, setTransactionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // State for server-side pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid is 0-indexed
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0); // Total number of rows

  // State for filters and sorting
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortingModel, setSortingModel] = useState<GridSortModel>();
  // New state for dedicated time sorting
  const [timeSortOrder, setTimeSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [filters, setFilters] = useState({
    searchQuery: "",
    productType: "",
    status: "",
    amountMin: "",
    amountMax: "",
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Build query parameters from filters, sorting, and pagination state
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: filterModel?.items.map(({ field, operator, value }) => ({
          field,
          type: columns?.find((col) => col.field === field)?.type || "string",
          operator,
          value,
        })),
        // Prioritize timeSortOrder, otherwise use the grid's sortingModel
        sortBy: timeSortOrder ? "createdAt" : sortingModel?.[0]?.field || "",
        sortOrder: timeSortOrder || sortingModel?.[0]?.sort || undefined,
        // Keep legacy filters for backward compatibility
        searchQuery: filters.searchQuery,
        productType: filters.productType,
        status: filters.status,
        amountMin: filters.amountMin,
        amountMax: filters.amountMax,
      };

      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.getTransactions,
        params,
      });
      
      // The backend now returns an object { data: [], totalCount: 0 }
      if (response && response.data) {
        console.log("Response " ,response)
        // Map data for the grid, adding a unique 'id', 'userName' and 'mobileNo'
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id, // DataGrid needs a unique 'id' field
          userName: item.userId ? item.userId.name : 'N/A',
          mobileNo: item.userId ? item.userId.mobileNo : 'N/A',
        }));

        setTransactionsData(mappedData);
        setRowCount(response.totalCount || 0);
      } else {
        setTransactionsData([]);
        setRowCount(0);
      }

    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to fetch transactions.");
    } finally {
      setLoading(false);
    }
  }, [filters, filterModel, sortingModel, paginationModel]); // Re-run when filters, sorting or paginationModel changes

  useEffect(() => {
    // Debounce the API call to avoid firing on every keystroke or rapid page change
    const timer = setTimeout(() => {
        fetchTransactions();
    }, 500);

    return () => clearTimeout(timer);

  }, [filters, filterModel, sortingModel, paginationModel]);

  // Expose pagination state and its setter to the component
  return { 
    transactionsData, 
    loading, 
    filters, 
    setFilters,
    setFilterModel,
    setSortingModel,
    rowCount,
    paginationModel,
    setPaginationModel, // This will be used as the onPaginationModelChange handler
    // Export the new handler and sort order
    handleSortByTime: () => {
      // When using this button, clear the grid's own sort model
      setSortingModel([]);
      setTimeSortOrder((prev) => {
        if (prev === "desc") return "asc";
        if (prev === "asc") return null; // Turn off
        return "desc"; // Start with descending
      });
    },
    timeSortOrder,
  };
};

export default useTransactions;
